/// <amd-module name="Wit/WorkItemForm/Common/Services/WorkItemFormAttachmentsService" />
import * as React from "react";
import { IObservableArray, ObservableArray } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { AttachmentReference } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient, IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { ILinkInfo } from "Wit/WorkItemDataManager/Contracts";
import { ConfirmSkippingOversizedAttachmentsDialog } from "Wit/WorkItemForm/Attachments/Dialogs/ConfirmSkippingOversizedAttachmentsDialog";
import { LinkTypeFieldIds } from "Wit/WorkItemLinksRegistration/Constants";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { Attachment } from "Wit/WorkItemModel/WorkItem/Links";
import { DalFields } from "Wit/Common/Generated/Constants";
import { Dialog } from "VSSUI/Dialog";
import * as Resources from "Wit/WorkItemForm/Resources";
import { format } from "VSS/Core/Util/String";

const defaultMaxAttachmentSize = 4194304;

interface IWorkItemSettings {
    maxAttachmentSize: number;
}

export interface IWorkItemFormAttachmentsService extends IVssService {
    /**
     * Returns an observable array of attachments. This will always be the same
     * object for the same work item, and it updates as the attachments on the
     * work item update.
     */
    getAttachmentsObservable(workItem: IWorkItem): IObservableArray<ILinkInfo>;
    getAttachments(workItem: IWorkItem): ILinkInfo[];
    attachFiles(workItem: IWorkItem, files: File[]): void;
    editComment(workItem: IWorkItem, attachment: ILinkInfo, newComment: string): void;
    deleteAttachment(workItem: IWorkItem, attachment: ILinkInfo): void;
}

class WorkItemFormAttachmentsService extends VssService implements IWorkItemFormAttachmentsService {
    private maxAttachmentSize = defaultMaxAttachmentSize;
    private witClient: IWorkItemTrackingRestClient;
    private modelService: IWorkItemModelService;
    private layoutManager: IVssLayoutManager;

    private attachmentsByWorkItem: Map<number, IObservableArray<ILinkInfo>>;

    /**
     * Files that have been sent to the server but have not yet completed
     */
    private placeholdersByFileByWorkItem: Map<number, Map<File, ILinkInfo>>;

    public async _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.witClient = getWorkItemTrackingClient(pageContext);
        this.modelService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        this.layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        this.attachmentsByWorkItem = new Map();
        this.placeholdersByFileByWorkItem = new Map();

        const cs = pageContext?.getService<IVssContributionService>("IVssContributionService");
        const settings = (await cs?.getDataAsync("ms.vss-work-web.work-item-common-configuration-data-provider")) as IWorkItemSettings | undefined;
        if (!settings) {
            return;
        }
        this.maxAttachmentSize = settings.maxAttachmentSize;
    }

    public getAttachmentsObservable(workItem: IWorkItem) {
        if (!this.attachmentsByWorkItem.has(workItem.id)) {
            const arr = new ObservableArray<ILinkInfo>(this.getAttachments(workItem));
            this.attachmentsByWorkItem.set(workItem.id, arr);
        }
        return this.attachmentsByWorkItem.get(workItem.id)!;
    }

    private setAttachments(workItem: IWorkItem, attachments: ILinkInfo[]) {
        if (this.attachmentsByWorkItem.has(workItem.id)) {
            this.attachmentsByWorkItem.get(workItem.id)!.value = attachments;
        } else {
            this.attachmentsByWorkItem.set(workItem.id, new ObservableArray<ILinkInfo>(attachments));
        }
    }

    public getAttachments(workItem: IWorkItem): ILinkInfo[] {
        const attachments = workItem
            .getLinks()
            .filter(l => l instanceof Attachment && !l.isRemoved())
            .map(a => a.linkData);
        this.setAttachments(workItem, attachments);
        return attachments;
    }

    public editComment(workItem: IWorkItem, attachment: ILinkInfo, newComment: string) {
        const matchingAttachment = workItem.getLinks().find(l => l instanceof Attachment && l.linkData.FilePath === attachment.FilePath);
        if (!matchingAttachment) {
            return;
        }

        matchingAttachment.setComment(newComment);
        workItem.evaluateDirty();
        this.getAttachments(workItem);
    }

    public deleteAttachment(workItem: IWorkItem, attachment: ILinkInfo) {
        const matchingAttachment = workItem.getLinks().find(l => l instanceof Attachment && l.linkData.FilePath === attachment.FilePath);
        if (!matchingAttachment) {
            return;
        }

        workItem.removeLinks([matchingAttachment], /* keepDeleted */ true);
        this.getAttachments(workItem);
    }

    public async attachFiles(workItem: IWorkItem, files: File[]) {
        if (workItem.isReadOnly()) {
            this.showErrorDialog(Resources.AttachmentUploadPermissionsError);
            return;
        }
        const oversizedFileNames: string[] = [];
        const filesToUpload: File[] = [];

        for (const file of files) {
            if (file.size > this.maxAttachmentSize) {
                oversizedFileNames.push(file.name);
            } else {
                filesToUpload.push(file);
            }
        }

        if (oversizedFileNames.length > 0 && !(await this.confirmSkippingOversizedAttachments(oversizedFileNames))) {
            return;
        }

        const areaPath = workItem.getFieldValueById(DalFields.AreaPathId);
        const failedFileNames: string[] = [];
        let noPermissionsError = false;

        await Promise.all(
            filesToUpload.map(async file => {
                this.createPlaceholder(workItem, file);
                try {
                    const attachmentReference = await this.witClient.createAttachment(
                        await file.arrayBuffer(),
                        workItem.project.guid,
                        file.name,
                        "Simple",
                        areaPath
                    );
                    this.onAttachmentCreated(workItem, file, attachmentReference);
                } catch (e) {
                    failedFileNames.push(file.name);
                    this.removePlaceholder(workItem, file);
                    if (Number(e.status) === 403) {
                        noPermissionsError = true;
                    }
                }
            })
        );

        if (noPermissionsError) {
            this.showErrorDialog(Resources.AttachmentUploadPermissionsError);
            return;
        }

        if (failedFileNames.length > 0) {
            this.showErrorDialog(format(Resources.FilesUploadError, failedFileNames.join(", ")));
        }
    }

    private showErrorDialog(message: string): void {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return (
                <Dialog onDismiss={onDismiss} titleProps={{ text: Resources.AttachmentUploadError }} showCloseButton={true}>
                    {message}
                </Dialog>
            );
        });
    }

    private createPlaceholder(workItem: IWorkItem, file: File) {
        const newPlaceholder = {
            FldID: LinkTypeFieldIds.Attachment,
            OriginalName: file.name,
            Length: file.size
        };

        if (!this.placeholdersByFileByWorkItem.has(workItem.id)) {
            this.placeholdersByFileByWorkItem.set(workItem.id, new Map());
        }
        this.placeholdersByFileByWorkItem.get(workItem.id)!.set(file, newPlaceholder);

        if (this.attachmentsByWorkItem.has(workItem.id)) {
            this.attachmentsByWorkItem.get(workItem.id)!.push(newPlaceholder);
        } else {
            this.setAttachments(workItem, [newPlaceholder]);
        }
    }

    private getPlaceholder(workItem: IWorkItem, file: File): ILinkInfo | undefined {
        return this.placeholdersByFileByWorkItem.get(workItem.id)?.get(file);
    }

    private replacePlaceholder(workItem: IWorkItem, file: File, replacement: ILinkInfo) {
        const placeholder = this.getPlaceholder(workItem, file);
        if (!placeholder) {
            return;
        }
        this.placeholdersByFileByWorkItem.get(workItem.id)!.delete(file);

        if (!this.attachmentsByWorkItem.has(workItem.id)) {
            return;
        }
        const attachments = this.attachmentsByWorkItem.get(workItem.id)!;
        const placeholderIndex = attachments.value.indexOf(placeholder);
        if (placeholderIndex >= 0) {
            attachments.splice(placeholderIndex, 1, replacement);
        } else {
            attachments.push(replacement);
        }
    }

    private removePlaceholder(workItem: IWorkItem, file: File) {
        const placeholder = this.getPlaceholder(workItem, file);
        if (!placeholder) {
            return;
        }
        this.placeholdersByFileByWorkItem.get(workItem.id)!.delete(file);

        if (!this.attachmentsByWorkItem.has(workItem.id)) {
            return;
        }
        const attachments = this.attachmentsByWorkItem.get(workItem.id)!;
        const placeholderIndex = attachments.value.indexOf(placeholder);
        if (placeholderIndex >= 0) {
            attachments.splice(placeholderIndex, 1);
        }
    }

    private confirmSkippingOversizedAttachments(oversizedFileNames: string[]): Promise<boolean> {
        return new Promise(resolve =>
            this.layoutManager.renderCallout(onDismiss => (
                <ConfirmSkippingOversizedAttachmentsDialog
                    oversidedAttachmentNames={oversizedFileNames}
                    maxFileSize={this.maxAttachmentSize}
                    onCancelUpload={() => {
                        resolve(false);
                        onDismiss();
                    }}
                    onUploadRemaining={() => {
                        resolve(true);
                        onDismiss();
                    }}
                />
            ))
        );
    }

    /**
     * After the uploaded file has been saved to the server, this function updates the work item to include the newly-added files.
     */
    private onAttachmentCreated(workItem: IWorkItem, file: File, attachmentReference: AttachmentReference) {
        const newAttachment = Attachment.create(file.name, attachmentReference.id, file.size, "");
        this.replacePlaceholder(workItem, file, newAttachment.linkData);

        workItem.addLinks([newAttachment]);

        if (!this.placeholdersByFileByWorkItem.has(workItem.id) || this.placeholdersByFileByWorkItem.get(workItem.id)!.size === 0) {
            this.getAttachments(workItem);
        }
    }
}

const serviceName = "IWorkItemFormAttachmentsService";
Services.add(serviceName, { serviceFactory: WorkItemFormAttachmentsService });
